/** Provider-backed model discovery and the selected swarm catalog.
 * Pulling imports models; selecting enables them. Provider configuration stays on Keys.
 */

import * as React from "react";
import { AlertTriangle, Eye, RefreshCw, Star, Unplug } from "lucide-react";
import { toast } from "react-toastify";
import { adminFetch } from "./adminFetch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui";

type Row = {
  value: string;
  label: string;
  tier: string;
  provider: string;
  wireId: string;
  engines: string[];
  vision: boolean;
  enabled: boolean;
  isDefault: boolean;
  inputPerMillion: number | null;
  outputPerMillion: number | null;
  sellInputPerMillion: number | null;
  sellOutputPerMillion: number | null;
  priceSource: string | null;
  priceVerifiedAt: string | null;
  deprecationNote: string | null;
  deprecationDate: string | null;
  providerMissingAt: string | null;
};

type FlagshipKeys = { anthropic: boolean; openai: boolean; google?: boolean };
type Provider = { key: string; label: string; kind: string; isActive: boolean; enabled: boolean; hasKey: boolean; canSyncModels: boolean };

const ENGINE_LABEL: Record<string, string> = { "claude-code": "Claude", codex: "Codex", native: "Native" };
const VENDOR_LABEL: Record<string, string> = { anthropic: "Anthropic", openai: "OpenAI", google: "Google" };

const money = (v: number | null) => (v == null ? "—" : `$${Number(v.toFixed(6))}`);

/** One row's price line: cost → sell, with staleness flagged, never hidden. */
function PriceLine({ r }: { r: Row }) {
  const days = r.priceVerifiedAt ? Math.floor((Date.now() - new Date(r.priceVerifiedAt).getTime()) / 86_400_000) : null;
  return (
    <span className="font-mono text-[11px] text-[var(--dash-text-muted)]">
      cost {money(r.inputPerMillion)}/{money(r.outputPerMillion)} per M
      {" · sell "}
      {money(r.sellInputPerMillion ?? r.inputPerMillion)}/{money(r.sellOutputPerMillion ?? r.outputPerMillion)}
      {r.priceSource === "manual" ? " · manual" : r.priceSource === "provider-api" ? " · provider price" : r.priceSource === "feed" ? " · community feed" : " · unverified"}
      {r.priceVerifiedAt && ` · checked ${new Date(r.priceVerifiedAt).toLocaleDateString()}`}
      {days != null && days > 30 && <span className="text-[#d92d43]"> · price {days}d old</span>}
    </span>
  );
}

/** Inline pricing editor — four numbers, one Save. Cost edits mark the row
 *  manual server-side so the feed won't overwrite them. */
function PricingEditor({ r, onSave, busy }: { r: Row; onSave: (patch: Record<string, number | null>) => void; busy: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [v, setV] = React.useState({
    inputPerMillion: r.inputPerMillion,
    outputPerMillion: r.outputPerMillion,
    sellInputPerMillion: r.sellInputPerMillion,
    sellOutputPerMillion: r.sellOutputPerMillion,
  });
  React.useEffect(() => {
    setV({ inputPerMillion: r.inputPerMillion, outputPerMillion: r.outputPerMillion, sellInputPerMillion: r.sellInputPerMillion, sellOutputPerMillion: r.sellOutputPerMillion });
  }, [r.inputPerMillion, r.outputPerMillion, r.sellInputPerMillion, r.sellOutputPerMillion]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-[11px] font-medium text-[var(--dash-text-muted)] hover:text-[var(--dash-primary)]">
        Edit pricing
      </button>
    );
  }
  const num = (x: number | null) => (x == null ? "" : String(x));
  const field = (key: keyof typeof v, label: string) => (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9.5px] uppercase tracking-wide text-[var(--dash-text-muted)]">{label}</span>
      <input
        type="number"
        min={0}
        step="0.01"
        value={num(v[key])}
        onChange={(e) => setV((s) => ({ ...s, [key]: e.target.value === "" ? null : Number(e.target.value) }))}
        className="w-20 rounded border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--dash-text)]"
      />
    </label>
  );
  return (
    <div className="flex flex-wrap items-end gap-2">
      {field("inputPerMillion", "cost in $/M")}
      {field("outputPerMillion", "cost out $/M")}
      {field("sellInputPerMillion", "sell in $/M")}
      {field("sellOutputPerMillion", "sell out $/M")}
      <button
        onClick={() => { onSave(v); setOpen(false); }}
        disabled={busy}
        className="rounded-md border border-[var(--dash-border)] px-2 py-1 text-[11px] font-medium text-[var(--dash-primary)] hover:border-[var(--dash-primary)]"
      >
        Save
      </button>
      <button onClick={() => setOpen(false)} className="px-1 py-1 text-[11px] text-[var(--dash-text-muted)] hover:text-[var(--dash-text)]">Cancel</button>
    </div>
  );
}

export function SwarmPanel() {
  const [rows, setRows] = React.useState<Row[]>([]);
  const [flagshipKeys, setFlagshipKeys] = React.useState<FlagshipKeys>({ anthropic: false, openai: false });
  const [providers, setProviders] = React.useState<Provider[]>([]);
  const [providerKey, setProviderKey] = React.useState("");
  const [view, setView] = React.useState("selected");
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [pullResult, setPullResult] = React.useState<{ provider: string; message: string } | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const [mRes, pRes] = await Promise.all([
        adminFetch("/api/admin/models"),
        adminFetch("/api/admin/providers"),
      ]);
      if (!mRes.ok || !pRes.ok) throw new Error("Failed to load the swarm");
      const m = await mRes.json();
      const p = await pRes.json();
      setRows((m.models || []).filter((r: Row) => (r.engines?.length ?? 0) > 0));
      if (m.flagshipKeys) setFlagshipKeys(m.flagshipKeys);
      const next: Provider[] = Array.isArray(p?.providers) ? p.providers : [];
      setProviders(next);
      setProviderKey((current) => next.some((p) => p.key === current && p.enabled) ? current
        : next.find((p) => p.enabled && p.isActive && p.kind !== "vendor")?.key
          ?? next.find((p) => p.enabled)?.key ?? "");
      setLoadError(false);
    } catch {
      setLoadError(true);
      toast.error("Failed to load the swarm");
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  const patch = async (value: string, p: Record<string, unknown>, busyKey?: string) => {
    setBusy(busyKey ?? value);
    try {
      const res = await adminFetch("/api/admin/models", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value, patch: p }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  /** Bulk toggle for the currently visible (search-filtered) rows — reuses
   *  the same per-row PATCH + validation the single-row checkbox uses (price
   *  gates, etc. still apply per row), just sequenced from one action so an
   *  admin doesn't have to click every model one by one after a fresh pull. */
  const patchMany = async (values: string[], enabled: boolean, busyKey: string) => {
    if (!values.length) return;
    setBusy(busyKey);
    let failed = 0;
    for (const value of values) {
      try {
        const res = await adminFetch("/api/admin/models", {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value, patch: { enabled } }),
        });
        if (!res.ok) failed++;
      } catch { failed++; }
    }
    await load();
    setBusy(null);
    if (failed) toast.error(`${values.length - failed} of ${values.length} updated — ${failed} failed (likely missing prices; edit those individually).`);
    else toast.success(`${enabled ? "Selected" : "Deselected"} ${values.length} model${values.length === 1 ? "" : "s"} for the swarm.`);
  };

  const post = async (busyKey: string, body: Record<string, unknown>, okMsg?: (j: any) => string) => {
    setBusy(busyKey);
    try {
      const res = await adminFetch("/api/admin/models", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.ok === false) throw new Error(j?.error || `HTTP ${res.status}`);
      if (okMsg) toast.success(okMsg(j));
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Request failed");
    } finally {
      setBusy(null);
    }
  };

  const openProviders = providers.filter((p) => p.kind !== "vendor");
  const activeLane = openProviders.find((p) => p.isActive && p.enabled) ?? null;
  const laneLabel = (key: string) =>
    providers.find((p) => p.key === key)?.label ?? VENDOR_LABEL[key] ?? key;

  const flagshipKeyMissing = (r: Row) => r.tier === "flagship" && !flagshipKeys[r.provider as keyof FlagshipKeys];
  const servable = (r: Row) => !r.providerMissingAt && (r.tier === "open"
    ? activeLane != null && r.provider === activeLane.key && activeLane.hasKey
    : !flagshipKeyMissing(r));
  const savedSelections = rows.filter((r) => r.enabled);
  const collected = savedSelections.filter(servable);
  const hiddenSelections = savedSelections.filter((r) => !servable(r));
  const selectedProvider = providers.find((p) => p.key === providerKey && p.enabled);
  const providerRows = rows.filter((r) => r.provider === selectedProvider?.key);
  const matchingRows = providerRows
    .filter((r) => `${r.label} ${r.wireId}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => Number(!!a.providerMissingAt) - Number(!!b.providerMissingAt));
  const hasCredential = selectedProvider && (selectedProvider.hasKey ||
    (selectedProvider.kind === "vendor" && flagshipKeys[selectedProvider.key as keyof FlagshipKeys]));
  const pullDisabledReason = !selectedProvider ? "Enable a provider on the Keys tab to get started."
    : !selectedProvider.canSyncModels ? "Model discovery is not supported for this provider configuration."
    : !hasCredential ? "Save this provider’s API key on the Keys tab first." : null;

  const pullModels = () => {
    if (!selectedProvider || pullDisabledReason) return;
    const chosen = selectedProvider;
    setPullResult(null);
    setQuery("");
    void post("sync:models", { action: "sync-provider", provider: chosen.key }, (j) => {
      const priceMessage = j.pricingWarning || (j.prices
        ? `${j.prices.updated} prices refreshed; ${j.prices.skippedManual} manual prices kept; ${j.prices.unmatched.length} prices not refreshed. ${(j.prices.warnings || []).join(" ")}`
        : "Pricing was not refreshed.");
      const message = `${chosen.label}: ${j.fetched ?? 0} models found — ${j.added ?? 0} new, ${j.updated ?? 0} updated${j.ghosted ? `, ${j.ghosted} unavailable models deselected` : ""}. ${priceMessage}`;
      setPullResult({ provider: chosen.key, message });
      return message;
    });
  };

  const ModelBadges = ({ r }: { r: Row }) => (
    <>
      {r.vision && <Eye className="h-2.5 w-2.5 text-[var(--dash-text-muted)]" aria-label="vision-capable" />}
      <span className="font-mono text-[10px] font-normal text-[var(--dash-text-muted)]">{r.engines.map((e) => ENGINE_LABEL[e] || e).join(" · ")}</span>
      {/* Provenance: a quiet badge, never a grouping — models are commodity. */}
      <span className="rounded-full bg-[var(--dash-bg-muted)] px-1.5 py-0.5 text-[9.5px] font-medium text-[var(--dash-text-muted)]">{laneLabel(r.provider)}</span>
      {(r.deprecationNote || r.deprecationDate) && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-[rgba(217,45,67,0.08)] px-1.5 py-0.5 text-[9.5px] font-medium text-[#d92d43]">
          <AlertTriangle className="h-2.5 w-2.5" /> delisting{r.deprecationDate ? ` ${new Date(r.deprecationDate).toLocaleDateString()}` : " soon"}
        </span>
      )}
    </>
  );

  return (
    <div>
      <p className="mb-4 text-[13px] text-[var(--dash-text-secondary)]">
        Choose which models the swarm can use. Enable providers and manage API keys on the <b>Keys</b> tab.
      </p>
      {loadError && <p role="alert" className="mb-3 text-sm text-[#d92d43]">Couldn’t load the catalog. <button className="underline" onClick={() => void load()}>Try again</button></p>}
      {loading ? <p role="status" className="py-6 text-sm text-[var(--dash-text-muted)]">Loading models…</p> : (
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="mb-4 flex w-full justify-start overflow-x-auto bg-[var(--dash-bg-subtle)]">
          <TabsTrigger value="selected">Selected models ({collected.length})</TabsTrigger>
          <TabsTrigger value="selection">Model selection</TabsTrigger>
          <TabsTrigger value="aa-router">Conductor</TabsTrigger>
        </TabsList>
        <TabsContent value="selected">
          {hiddenSelections.length > 0 && (
            <div className="mb-3 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] p-3 text-sm">
              <p className="text-[var(--dash-text-secondary)]">{hiddenSelections.length} saved selections are hidden because their provider is disabled, disconnected or missing a key. Enable or reconnect the provider to show them again.</p>
              <button disabled={busy != null} onClick={() => void post("cleanup", { action: "clear-model-selections", values: hiddenSelections.map((r) => r.value) }, (j) => `${j.count} hidden selections cleared. Models remain in their provider catalogs.`)}
                className="mt-2 rounded-md border border-[var(--dash-border)] px-3 py-1.5 font-medium text-[var(--dash-text-secondary)] disabled:opacity-50">
                {busy === "cleanup" ? "Clearing…" : `Clear hidden selections (${hiddenSelections.length})`}
              </button>
              <p className="mt-1 text-xs text-[var(--dash-text-muted)]">Optional cleanup: clears these saved choices. You can select the models again later.</p>
            </div>
          )}
          <div className="mb-2 flex items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Selected for the swarm ({collected.length})</p>
            <button
              onClick={() => void post("sync:prices", { action: "sync-prices", values: collected.map((r) => r.value), preserveManual: true }, (j) => `${j.updated} prices refreshed; ${j.skippedManual} manual prices kept; ${j.unmatched.length} not refreshed. ${(j.warnings || []).join(" ")}`)}
              disabled={busy != null || collected.length === 0}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--dash-border)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--dash-text-secondary)] hover:border-[var(--dash-primary)] hover:text-[var(--dash-primary)] disabled:opacity-50"
              title="Refresh selected models from each provider’s pricing source. Manual costs and sell prices are preserved."
            >
              <RefreshCw className={`h-2.5 w-2.5 ${busy === "sync:prices" ? "animate-spin" : ""}`} />
              Refresh selected prices
            </button>
          </div>

          {collected.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[var(--dash-border)] px-4 py-10 text-center">
              <p className="text-[13px] font-medium text-[var(--dash-text-secondary)]">No selected models from connected providers.</p>
              <p className="mt-1 text-[12px] text-[var(--dash-text-muted)]">Open Model selection, choose a provider and select models for the swarm.</p>
            </div>
          ) : (
            <div
              role="region"
              aria-label="Selected models list"
              tabIndex={0}
              className="grid max-h-[min(50dvh,560px)] gap-1.5 overflow-y-auto overscroll-contain rounded-lg pr-2 [scrollbar-gutter:stable] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-primary)]"
            >
              {collected.map((r) => {
                const dead = !servable(r);
                return (
                  <div key={r.value} className={`rounded-lg border px-3.5 py-2.5 ${dead ? "border-dashed border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] opacity-80" : "border-[var(--dash-border)] bg-[var(--dash-panel-bg)]"}`}>
                    <div className="flex items-center gap-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-semibold text-[var(--dash-text)]">
                          {r.label}
                          <ModelBadges r={r} />
                          {r.isDefault && r.tier === "flagship" && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--dash-primary-tint)] px-1.5 py-0.5 text-[9.5px] font-semibold text-[var(--dash-primary)]" title="The router's first pick for this engine when the swarm can't serve — the swarm still decides per run">
                              <Star className="h-2.5 w-2.5" /> engine first pick
                            </span>
                          )}
                          {dead && (
                            <span
                              className="inline-flex items-center gap-0.5 rounded-full bg-[rgba(217,45,67,0.08)] px-1.5 py-0.5 text-[9.5px] font-medium text-[#d92d43]"
                              title={r.tier === "flagship"
                                ? `The ${laneLabel(r.provider)} key is disabled or missing on Keys — no run can use this model until it's enabled there.`
                                : `Its lane (${laneLabel(r.provider)}) is not the connected one — the router never serves it. Reconnect the lane on Keys, or Remove it.`}
                            >
                              <Unplug className="h-2.5 w-2.5" /> {r.tier === "flagship" ? "not servable — key disabled (Keys)" : "not servable — lane disconnected"}
                            </span>
                          )}
                        </p>
                        <PriceLine r={r} />
                      </div>
                      {r.tier === "flagship" && !r.isDefault && (
                        <button
                          onClick={() => void patch(r.value, { isDefault: true })}
                          disabled={busy != null}
                          className="rounded-md border border-[var(--dash-border)] px-2 py-1 text-[11px] font-medium text-[var(--dash-text-secondary)] hover:border-[var(--dash-primary)] hover:text-[var(--dash-primary)]"
                          title="Make this the engine's flagship first pick"
                        >
                          Make first pick
                        </button>
                      )}
                      <button
                        onClick={() => void patch(r.value, { enabled: false })}
                        disabled={busy != null}
                        title="Take out of the swarm — the row stays for usage history, it just stops serving"
                        className="rounded-md px-2 py-1 text-[11px] font-medium text-[var(--dash-text-muted)] hover:text-[#d92d43]"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-1.5">
                      <PricingEditor r={r} busy={busy != null} onSave={(p) => void patch(r.value, p)} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
        <TabsContent value="selection">
          <div className="mb-4 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] p-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-sm font-medium text-[var(--dash-text)]">
                Provider
                <select value={providerKey} disabled={busy != null} onChange={(e) => { setProviderKey(e.target.value); setQuery(""); }}
                  className="min-h-10 rounded-md border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-3 text-sm">
                  {!selectedProvider && <option value="">Choose an enabled provider</option>}
                  {providers.filter((p) => p.enabled).map((p) => <option key={p.key} value={p.key}>{p.label}{p.kind !== "vendor" && !p.isActive ? " — not connected" : ""}</option>)}
                </select>
              </label>
              <button onClick={pullModels} disabled={busy != null || !!pullDisabledReason}
                className="inline-flex min-h-10 items-center gap-2 rounded-md bg-[var(--dash-primary)] px-4 text-sm font-medium text-white disabled:opacity-50"
                title={pullDisabledReason ?? "Fetch this provider’s models and refresh their cost prices"}>
                <RefreshCw aria-hidden="true" className={`h-4 w-4 ${busy === "sync:models" ? "animate-spin" : ""}`} />
                {busy === "sync:models" ? "Pulling models…" : selectedProvider ? `Pull ${selectedProvider.label} models & prices` : "Pull models & prices"}
              </button>
            </div>
            <p className="mt-3 text-sm text-[var(--dash-text-muted)]">{pullDisabledReason ?? "Pull models and cost prices for this provider, then select models below. Manual costs and your sell prices stay unchanged."}</p>
            {selectedProvider?.kind !== "vendor" && selectedProvider && !selectedProvider.isActive && (
              <p className="mt-2 text-sm text-[var(--dash-text-secondary)]">Connect {selectedProvider.label} on the Keys tab before selecting its models. The swarm currently uses one open-model provider at a time.</p>
            )}
            {pullResult?.provider === providerKey && <p role="status" className="mt-2 text-sm text-[var(--dash-text)]">{pullResult.message}</p>}
          </div>
          {selectedProvider && (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-[var(--dash-text-secondary)]">{selectedProvider.label} · {providerRows.length} models · {providerRows.filter((r) => r.enabled).length} selected</p>
                <input type="search" aria-label="Search provider models" placeholder="Search models" value={query} onChange={(e) => setQuery(e.target.value)}
                  className="min-h-10 rounded-md border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-3 text-sm text-[var(--dash-text)]" />
              </div>
              {matchingRows.length > 0 && (() => {
                const servableMatches = matchingRows.filter(servable);
                const toSelect = servableMatches.filter((r) => !r.enabled);
                const toDeselect = matchingRows.filter((r) => r.enabled);
                return (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => void patchMany(toSelect.map((r) => r.value), true, "select:all")}
                      disabled={busy != null || toSelect.length === 0}
                      className="rounded-md border border-[var(--dash-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--dash-text-secondary)] hover:border-[var(--dash-primary)] hover:text-[var(--dash-primary)] disabled:opacity-50"
                      title={query ? "Select every model matching your search that has a usable price" : "Select every model in this list that has a usable price"}
                    >
                      {busy === "select:all" ? "Selecting…" : `Select all${query ? " matching" : ""} (${servableMatches.length})`}
                    </button>
                    <button
                      onClick={() => void patchMany(toDeselect.map((r) => r.value), false, "select:none")}
                      disabled={busy != null || toDeselect.length === 0}
                      className="rounded-md px-3 py-1.5 text-[12px] font-medium text-[var(--dash-text-muted)] hover:text-[#d92d43] disabled:opacity-50"
                    >
                      {busy === "select:none" ? "Clearing…" : "Deselect all"}
                    </button>
                    {servableMatches.length < matchingRows.length && (
                      <span className="text-[11px] text-[var(--dash-text-muted)]">{matchingRows.length - servableMatches.length} not selectable, skipped</span>
                    )}
                  </div>
                );
              })()}
              {matchingRows.length === 0 ? (
                <p className="rounded-lg border border-dashed border-[var(--dash-border)] p-8 text-center text-sm text-[var(--dash-text-muted)]">
                  {providerRows.length ? "No models match your search." : `No compatible models saved for ${selectedProvider.label}. Pull models to check its catalog.`}
                </p>
              ) : (
                <div
                  key={providerKey}
                  role="region"
                  aria-label={`${selectedProvider.label} models list`}
                  tabIndex={0}
                  className="grid max-h-[min(50dvh,560px)] gap-2 overflow-y-auto overscroll-contain rounded-lg pr-2 [scrollbar-gutter:stable] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-primary)]"
                >
                  {matchingRows.map((r) => (
                    <div key={r.value} className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] p-3">
                      <div className="flex items-start gap-3">
                        <input type="checkbox" id={`select-${r.value}`} checked={r.enabled} disabled={busy != null || (!r.enabled && !servable(r))}
                          onChange={(e) => void patch(r.value, { enabled: e.target.checked })}
                          className="mt-1 h-4 w-4 accent-[var(--dash-primary)]"
                          aria-label={`Select ${r.label} for the swarm`} />
                        <div className="min-w-0 flex-1">
                          <label htmlFor={`select-${r.value}`} className="block text-sm font-medium text-[var(--dash-text)]">{r.label}</label>
                          <div className="mt-1 flex flex-wrap items-center gap-2"><ModelBadges r={r} /></div>
                          {r.providerMissingAt && (
                            <p className="mt-1 text-[11.5px] text-[#d92d43]">Not offered by {laneLabel(r.provider)} anymore (last pull {new Date(r.providerMissingAt).toLocaleDateString()}). It comes back if a later pull finds it.</p>
                          )}
                          <PriceLine r={r} />
                          <div className="mt-2"><PricingEditor r={r} busy={busy != null} onSave={(p) => void patch(r.value, p)} /></div>
                        </div>
                        <span className="text-sm text-[var(--dash-text-muted)]">{r.providerMissingAt ? "Not offered" : r.enabled ? "Selected" : "Not selected"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-3 text-sm text-[var(--dash-text-muted)]">Provider prices are used where supported; other prices use a labeled community feed. Missing prices stay unverified. Fireworks prices use Standard serverless rates.</p>
              <p className="mt-3 text-sm text-[var(--dash-text-muted)]">Selections save immediately. If a model needs pricing before it can be selected, edit its pricing or refresh prices in Selected models.</p>
            </>
          )}
        </TabsContent>
        <TabsContent value="aa-router">
          <AaRouterTab active={view === "aa-router"} />
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}

// ── AA Router operator surface ────────────────────────────────────────────────
// Read-only view of the always-on control plane (GET /api/admin/aa-router):
// live route health, a decision explorer, and
// the matched benchmark report with explicit "insufficient sample" states. No
// customer-facing surface exposes these controls.

type AaRouterSummary = {
  status: { mode: string; configuredVia: string; pilotProjects: string[]; policyVersion: string; stateVersion: string; jevModel: string };
  /** Internal route-health rows. Historical API field name retained for compatibility. */
  qualifications: Array<{ modelValue: string; engine: string; protocol: string; status: string; capabilityClass: string; supportedEfforts: string[]; vision: boolean; usageReporting: boolean; fixtureVersion?: string | null; testedAt: string | null; failureReason: string | null }>;
  health: { windowDays: number; decisions: number; calls: number; successfulCalls: number; servedModels: number; usageCompletenessPct: number; unpricedCalls: number; leaseReusePct: number; jevFallbacks: number; jevErrorRatePct: number; jevLatencyP50Ms: number | null; jevLatencyP95Ms: number | null; routerModelCostUsd: number; contextReceipts: number; contextLinkedToRoutes: number; failedContextTurns: number; averageInjectedContextChars: number; toolCalls: number; repeatedToolReads: number; repeatedToolReadPct: number; contextPreflightP50Ms: number | null; plannerTurnP50Ms: number | null };
  decisions: Array<{ decisionId: string; createdAt: string; lane: string; engine: string; transition: string; mode: string; benchmarkArm: string; selectedModel: string | null; servedModel: string | null; selectedModelLabel: string | null; servedModelLabel: string | null; selectedEffort: string | null; effortApplied: string | null; modelApplied: boolean; applyReason: string | null; fallbackReason: string | null; jevLatencyMs: number | null; contextMode: string | null; injectedContextChars: number | null; capsuleChars: number | null; toolCalls: number | null; repeatedToolReads: number | null }>;
  benchmark: { arms: Array<{ arm: string; decisions: number; calls: number; distinctModels: number; totalModelCostUsd: number; anyUntrusted: boolean }>; savings: { sufficient: boolean; reason: string; savingsVsFlagshipPct?: number }; note: string };
};

function Tile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-3 py-2">
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">{label}</div>
      <div className="mt-0.5 text-[15px] font-semibold text-[var(--dash-text)]">{value}</div>
      {hint && <div className="text-[11px] text-[var(--dash-text-muted)]">{hint}</div>}
    </div>
  );
}

function AaRouterTab({ active }: { active: boolean }) {
  const [data, setData] = React.useState<AaRouterSummary | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await adminFetch("/api/admin/aa-router?days=30");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setErr(e instanceof Error ? e.message : "load failed"); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { if (active && !data && !loading) void load(); }, [active, data, loading, load]);

  if (loading && !data) return <p role="status" className="py-6 text-sm text-[var(--dash-text-muted)]">Loading Conductor…</p>;
  if (err) return <p role="alert" className="py-4 text-sm text-[#d92d43]">Couldn’t load Conductor. <button className="underline" onClick={() => void load()}>Retry</button> <span className="text-[var(--dash-text-muted)]">({err})</span></p>;
  if (!data) return null;
  const h = data.health;
  const routeIssues = data.qualifications.filter((q) =>
    (q.status === "failed" && q.fixtureVersion === "runtime-unavailable-v1")
    || (q.status === "suspended" && q.fixtureVersion === "runtime-degraded-v1")
  );
  const latestDecisionAt = data.decisions[0]?.createdAt ? new Date(data.decisions[0].createdAt) : null;
  const routingActive = h.successfulCalls > 0 && h.servedModels > 0;

  return (
    <div className="grid gap-4">
      <div>
        <p className="text-[13px] font-semibold text-[var(--dash-text)]">Conductor</p>
        <p className="mt-0.5 text-[12px] text-[var(--dash-text-secondary)]">Jev chooses the best sufficient model from the models selected in Swarm. Conductor applies that choice, keeps clean tool chains together, and records what actually served.</p>
      </div>
      <div className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg-subtle)] p-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[13px] font-semibold text-[var(--dash-text)]">Always on</span>
          <span className="text-[11px] text-[var(--dash-text-muted)]">Planner, Build, QA, Developer and Workspace calls all use the selected Swarm.</span>
          <button onClick={() => void load()} className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--dash-border)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--dash-text-secondary)] hover:border-[var(--dash-primary)] hover:text-[var(--dash-primary)]"><RefreshCw className={`h-2.5 w-2.5 ${loading ? "animate-spin" : ""}`} />Refresh</button>
        </div>
        <div className="mt-1 text-[11px] text-[var(--dash-text-muted)]">Provider sync and your Swarm selection define what Jev may choose. Failed routes rest automatically while another selected model takes over.</div>
      </div>

      <div className={`rounded-lg border p-3 ${routingActive ? "border-[rgba(35,134,95,0.35)] bg-[rgba(35,134,95,0.06)]" : "border-[rgba(184,134,11,0.35)] bg-[rgba(255,234,0,0.08)]"}`}>
        <p className="text-[12px] font-semibold text-[var(--dash-text)]">{routingActive ? "Routing is active" : h.decisions > 0 ? "Waiting for a successful model call" : "No routing activity yet"}</p>
        <p className="mt-1 text-[11.5px] text-[var(--dash-text-secondary)]">
          {routingActive ? `${h.successfulCalls} successful calls across ${h.servedModels} served ${h.servedModels === 1 ? "model" : "models"}.` : "Conductor will use the selected Swarm as soon as an agent runs."}
          {latestDecisionAt ? ` Latest decision ${latestDecisionAt.toLocaleString()}.` : ""}
          {routeIssues.length ? ` ${routeIssues.length} ${routeIssues.length === 1 ? "route is" : "routes are"} resting after a runtime problem; other Swarm models remain available.` : ""}
        </p>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Live health · last {h.windowDays}d</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile label="Decisions" value={h.decisions} hint={`${h.jevFallbacks} fallbacks · ${h.jevErrorRatePct}% jev err`} />
          <Tile label="Model calls" value={h.calls} hint={`${h.usageCompletenessPct}% with usage`} />
          <Tile label="Models served" value={h.servedModels} hint={`${h.successfulCalls} successful calls`} />
          <Tile label="Calls missing price" value={h.unpricedCalls} />
          <Tile label="Model continuity" value={`${h.leaseReusePct}%`} hint="kept the same model" />
          <Tile label="Typical decision" value={h.jevLatencyP50Ms != null ? `${h.jevLatencyP50Ms}ms` : "—"} />
          <Tile label="Slow decision" value={h.jevLatencyP95Ms != null ? `${h.jevLatencyP95Ms}ms` : "—"} />
          <Tile label="Routing cost" value={`$${h.routerModelCostUsd.toFixed(4)}`} hint="recorded calls" />
          <Tile label="Context receipts" value={h.contextReceipts} hint={`${h.contextLinkedToRoutes} linked to routes`} />
          <Tile label="Context per turn" value={h.averageInjectedContextChars ? `${Math.round(h.averageInjectedContextChars / 100) / 10}k chars` : "—"} hint={`${h.failedContextTurns} failed turns`} />
          <Tile label="Tool reads" value={h.toolCalls} hint={`${h.repeatedToolReads} repeated`} />
          <Tile label="Repeated work" value={`${h.repeatedToolReadPct}%`} hint="same tool and target in a turn" />
          <Tile label="Context setup" value={h.contextPreflightP50Ms != null ? `${h.contextPreflightP50Ms}ms` : "—"} hint="typical Planner preflight" />
          <Tile label="Planner turn" value={h.plannerTurnP50Ms != null ? `${Math.round(h.plannerTurnP50Ms / 100) / 10}s` : "—"} hint="typical completed turn" />
        </div>
      </div>

      {routeIssues.length > 0 && (
        <details className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-3 py-2">
          <summary className="cursor-pointer text-[12px] font-medium text-[var(--dash-text)]">{routeIssues.length} resting {routeIssues.length === 1 ? "route" : "routes"}</summary>
          <div className="mt-2 grid gap-1.5">
            {routeIssues.map(q => (
              <div key={`${q.modelValue}:${q.engine}:${q.protocol}`} className="rounded-md bg-[var(--dash-bg-subtle)] px-2.5 py-2 text-[11.5px]">
                <div className="font-medium text-[var(--dash-text)]">{q.modelValue}</div>
                <div className="text-[var(--dash-text-muted)]">{q.engine} could not use this route reliably. Conductor removed it from automatic routing and is using another selected Swarm model. Re-enable the model only after the provider route is healthy.</div>
              </div>
            ))}
          </div>
        </details>
      )}

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Cost evidence</p>
        <div className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] p-3">
          <div className="text-[12px]">
            {data.benchmark.savings.sufficient
              ? <span className="font-semibold text-[var(--dash-primary)]">Conductor used {data.benchmark.savings.savingsVsFlagshipPct?.toFixed(1)}% less model cost per accepted result than fixed flagship routing.</span>
              : <span className="text-[var(--dash-text-secondary)]">Cost is being recorded, but there are not yet enough matched accepted results to claim savings against fixed flagship routing.</span>}
          </div>
          {data.benchmark.arms.length > 0 && (
            <details className="mt-2 border-t border-[var(--dash-border)] pt-2">
              <summary className="cursor-pointer text-[11px] font-medium text-[var(--dash-text-muted)]">Recorded activity</summary>
              <div className="mt-2 grid gap-1.5">
                {data.benchmark.arms.map(a => (
                  <div key={a.arm} className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px] text-[var(--dash-text-secondary)]">
                    <span>{a.decisions} decisions</span><span>{a.calls} calls</span><span>{a.distinctModels} models</span><span>${a.totalModelCostUsd.toFixed(4)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--dash-text-muted)]">Decision explorer · latest {data.decisions.length}</p>
        {data.decisions.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[var(--dash-border)] px-4 py-6 text-center text-[12px] text-[var(--dash-text-muted)]">No routing decisions recorded in this window.</p>
        ) : (
          <div className="grid max-h-[40dvh] gap-1 overflow-y-auto rounded-lg pr-1">
            {data.decisions.map((d) => (
              <div key={d.decisionId} className="rounded-md border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] px-2.5 py-1.5 text-[11.5px]">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="font-semibold text-[var(--dash-text)]">{d.lane}/{d.engine}</span>
                  <span className="text-[var(--dash-text-secondary)]">{d.transition}</span>
                  <span className="rounded bg-[var(--dash-bg-subtle)] px-1 text-[10px] uppercase text-[var(--dash-text-muted)]">{d.mode}·{d.benchmarkArm}</span>
                  <span className="text-[var(--dash-text-secondary)]">→ {d.servedModelLabel || d.selectedModelLabel || d.servedModel || d.selectedModel || "(no route)"}{d.selectedEffort ? ` · ${d.selectedEffort}` : ""}</span>
                  {d.servedModel
                    ? <span className="text-[var(--dash-primary)]">served</span>
                    : d.selectedModel
                      ? <span className="text-[var(--dash-text-muted)]">selected</span>
                      : <span className="text-[#d92d43]">not routed</span>}
                  {d.servedModel && d.selectedModel && d.servedModel !== d.selectedModel && <span className="text-[var(--dash-text-muted)]">Jev selected {d.selectedModelLabel || d.selectedModel}; runtime rerouted</span>}
                  {d.fallbackReason && <span className="text-[#b8860b]">fallback: {d.fallbackReason}</span>}
                  {!d.selectedModel && d.applyReason && <span className="text-[var(--dash-text-muted)]">{d.applyReason}</span>}
                  {d.jevLatencyMs != null && <span className="ml-auto text-[var(--dash-text-muted)]">{d.jevLatencyMs}ms</span>}
                  {d.contextMode && <span className="text-[var(--dash-text-muted)]">context {d.contextMode}{d.injectedContextChars != null ? ` · ${d.injectedContextChars} chars` : ""}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
